<script>
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue";

import { useResizeObserver } from "@/abc-dsk/composables/useResizeObserver";
import { mediaWatchEventListenerPonyfill } from "@/polyfills";

export default defineComponent({
  name: "TgTooltip",
  props: {
    hideOnTargetClick: {
      type: Boolean,
      default: false
    },
    marginBottom: {
      type: Number,
      default: 0
    },
    text: {
      type: String,
      default: ""
    },
    tooltipContainerClass: {
      type: [String, Array, Object],
      default: null
    },
    tooltipContentClass: {
      type: [String, Array, Object],
      default: null
    },
    tooltipArrowClass: {
      type: [String, Array, Object],
      default: null
    }
  },
  emits: ["show", "hide"],
  setup(props, { emit }) {
    const POSITION_TOP_DEFAULT = -1000;
    const POSITION_LEFT_DEFAULT = -1000;

    const isVisible = ref(false);

    const canDeviceHover = ref(null);
    const onDeviceHoverChange = (mq) => (canDeviceHover.value = mq.matches === true);
    const deviceHoverMediaWatch = window.matchMedia("(any-hover: hover)");

    mediaWatchEventListenerPonyfill.addEventListener(deviceHoverMediaWatch, onDeviceHoverChange);

    // Set initial device hover type
    onMounted(() => onDeviceHoverChange(deviceHoverMediaWatch));

    onBeforeUnmount(() => {
      mediaWatchEventListenerPonyfill.removeEventListener(
        deviceHoverMediaWatch,
        onDeviceHoverChange
      );
    });

    /** @type import("@vue/reactivity").Ref<HTMLElement> */
    const triggerEl = ref();

    /** @type import("@vue/reactivity").Ref<HTMLElement> */
    const contentEl = ref();

    /** @type import("@vue/reactivity").Ref<HTMLElement> */
    const arrowEl = ref();

    const position = reactive({
      top: POSITION_TOP_DEFAULT,
      left: POSITION_LEFT_DEFAULT
    });

    const setPosition = (top, left) => {
      position.top = top;
      position.left = left;
    };

    const resetPosition = () => {
      setPosition(POSITION_TOP_DEFAULT, POSITION_LEFT_DEFAULT);
    };

    const updatePosition = () => {
      const elementConstraints = triggerEl.value?.getBoundingClientRect();
      const contentConstraints = contentEl.value?.getBoundingClientRect();

      if (elementConstraints === undefined || contentConstraints === undefined) {
        return resetPosition();
      }

      // If unsupported position somehow gets through
      let top = POSITION_TOP_DEFAULT;
      let left = POSITION_LEFT_DEFAULT;

      // Position is static for now as we only need top-tooltips
      const offsetHeight = contentConstraints.height + props.marginBottom;
      const offsetWidth = (contentConstraints.width - elementConstraints.width) / 2;
      top = elementConstraints.top - offsetHeight + window.scrollY;
      left = elementConstraints.left - offsetWidth + window.scrollX;

      // Prevent tooltip from going off-screen in Grammatip iframe
      if (left < 0) {
        left = 0;
        const arrowConstraints = arrowEl.value?.getBoundingClientRect();
        // This runs twice, once before the arrow is actually on screen - we don't want to move it then.
        if (arrowConstraints.left > 0) {
          const offset =
            elementConstraints.left - (elementConstraints.width - arrowConstraints.width) / 2;
          arrowEl.value?.style.setProperty("left", `${offset}px`);
        }
      } else {
        // Make sure arrow is centered when resizing
        arrowEl.value?.style.setProperty("left", "");
      }

      setPosition(top, left);
    };

    const updatePositionAnimation = () => {
      requestAnimationFrame(updatePosition);
    };

    const show = () => {
      // Prevent double emit and re-calculation of position.
      if (isVisible.value) {
        return;
      }

      // If we need to hide the tooltip when the target is clicked,
      // we will not ever be able to show the tooltip on a device that can't hover.
      if (props.hideOnTargetClick && !canDeviceHover.value) {
        return;
      }

      isVisible.value = true;
      updatePositionAnimation();
      emit("show");
    };

    const hide = () => {
      isVisible.value = false;
      resetPosition();
      emit("hide");
    };

    // React properly to window size changes
    const bodyElement = ref(document.body);
    const { width, height } = useResizeObserver(bodyElement);
    watch([width, height], updatePositionAnimation);

    // Respond to content changes
    const contentObserver = new ResizeObserver(() => {
      updatePositionAnimation();
    });
    onUnmounted(() => {
      contentObserver.disconnect();
    });
    watch(isVisible, async (value) => {
      if (value) {
        await nextTick(); // Make sure the contentEl ref is rendered and not null.
        contentObserver.observe(contentEl.value);
      } else {
        contentObserver.unobserve(contentEl.value);
      }
    });

    return {
      position,
      triggerEl,
      contentEl,
      arrowEl,
      canDeviceHover,
      isVisible,

      show,
      hide
    };
  }
});
</script>

<template>
  <div class="tg-tooltip">
    <div
      ref="triggerEl"
      @mouseover="show()"
      @mouseleave="hide()"
      @blur="hide()"
      @click="hideOnTargetClick && hide()"
    >
      <slot />
    </div>
    <Teleport to="body">
      <div
        v-if="isVisible"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
        class="tg-tooltip__content-container"
        :class="tooltipContainerClass"
      >
        <div ref="contentEl" class="tg-tooltip__content" :class="tooltipContentClass">
          <slot name="content">{{ text }}</slot>
        </div>
        <div ref="arrowEl" class="tg-tooltip__arrow" :class="tooltipArrowClass"></div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@z-index: 1100;

.tg-tooltip {
  display: inline-flex;
  position: relative;

  &__content-container {
    position: absolute;
    z-index: @z-index;
    max-width: 250px;
    text-align: center;
    pointer-events: none;
  }

  &__content {
    padding: 10px 15px;
    border-radius: 2px;
    background-color: white;
    box-shadow: 2px 2px 8px 0 rgba(0, 0, 0, 0.2);
    font-size: 12px;
    line-height: 17px;
  }

  &__arrow {
    position: absolute;
    z-index: @z-index;
    bottom: -13px;
    left: calc(50% - 14px);
    width: 0;
    height: 0;
    margin: 6px;
    border-width: 8px 8px 0 8px;
    border-style: solid;
    border-top-color: @colors[@white];
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }
}
</style>
